import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Check, AlertCircle, Star, Bell, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { accountsAPI, cartAPI, reviewsAPI, stockAlertsAPI } from '@/utils/api';
import { toast } from 'sonner';

const AccountDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [alertSubscribed, setAlertSubscribed] = useState(false);
  const [subscribingAlert, setSubscribingAlert] = useState(false);

  useEffect(() => {
    loadAccount();
    loadReviews();
  }, [id]);

  const loadAccount = async () => {
    try {
      const response = await accountsAPI.getById(id);
      setAccount(response.data);
    } catch (error) {
      toast.error('Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        reviewsAPI.getByAccount(id),
        reviewsAPI.getStats(id)
      ]);
      setReviews(reviewsRes.data);
      setReviewStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load reviews');
    }
  };

  const handleAddToCart = async () => {
    if (!localStorage.getItem('token')) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    setAddingToCart(true);
    try {
      await cartAPI.add({ account_id: account.account_id, quantity: 1 });
      toast.success('Added to cart!');
      setTimeout(() => navigate('/cart'), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleStockAlert = async () => {
    if (!localStorage.getItem('token')) {
      toast.error('Please login to set stock alerts');
      navigate('/login');
      return;
    }

    setSubscribingAlert(true);
    try {
      await stockAlertsAPI.create(account.account_id);
      setAlertSubscribed(true);
      toast.success('You will be notified when this product is back in stock!');
    } catch (error) {
      if (error.response?.data?.message?.includes('Already subscribed')) {
        setAlertSubscribed(true);
      }
      toast.error(error.response?.data?.detail || 'Failed to set stock alert');
    } finally {
      setSubscribingAlert(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!localStorage.getItem('token')) {
      toast.error('Please login to write a review');
      navigate('/login');
      return;
    }

    setSubmittingReview(true);
    try {
      await reviewsAPI.create({
        account_id: account.account_id,
        rating: newReview.rating,
        comment: newReview.comment
      });
      toast.success('Review submitted!');
      setShowReviewForm(false);
      setNewReview({ rating: 5, comment: '' });
      loadReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-600'
            } ${interactive ? 'cursor-pointer hover:text-yellow-400' : ''}`}
            onClick={() => interactive && onRate && onRate(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 text-center">
          <p className="text-muted-foreground">Account not found</p>
        </div>
      </div>
    );
  }

  const isOutOfStock = account.stock === 0;

  return (
    <div className="min-h-screen bg-background" data-testid="account-detail-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-secondary aspect-video lg:aspect-square">
              <img
                src={account.image_url}
                alt={account.title}
                className={`w-full h-full object-cover ${isOutOfStock ? 'opacity-50' : ''}`}
                data-testid="account-image"
              />
              {/* Coming Soon Badge for Out of Stock */}
              {isOutOfStock && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/80 backdrop-blur-sm px-6 py-3 rounded-full border border-white/20">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-yellow-500" />
                      <span className="text-xl font-heading font-black text-yellow-500" data-testid="coming-soon-badge">
                        COMING SOON
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Details */}
            <div>
              <div className="mb-6">
                {account.featured && (
                  <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-4">
                    Featured
                  </span>
                )}
                <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-4" data-testid="account-title">
                  {account.title}
                </h1>
                <p className="text-3xl font-heading font-black text-primary" data-testid="account-price">
                  ₹{account.price}
                </p>
              </div>
              
              <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                {account.stock > 0 ? (
                  <div className="flex items-center gap-2 text-accent" data-testid="in-stock-badge">
                    <Check className="w-5 h-5" />
                    <span className="font-bold">In Stock ({account.stock} available)</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-500" data-testid="out-of-stock-badge">
                    <Clock className="w-5 h-5" />
                    <span className="font-bold">Out of Stock - Coming Soon</span>
                  </div>
                )}
              </div>
              
              {/* Rating Summary */}
              {reviewStats && reviewStats.total_reviews > 0 && (
                <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-heading font-black">{reviewStats.average_rating}</div>
                    <div>
                      {renderStars(Math.round(reviewStats.average_rating))}
                      <p className="text-sm text-muted-foreground">{reviewStats.total_reviews} reviews</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mb-8">
                <h2 className="text-xl font-heading font-bold mb-3">Description</h2>
                <p className="text-muted-foreground leading-relaxed" data-testid="account-description">
                  {account.description}
                </p>
              </div>
              
              <div className="mb-8 p-4 rounded-xl bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-bold text-primary">Account Validity: {account.validity_days} Days</span>
                </div>
              </div>
              
              <div className="flex gap-4">
                {isOutOfStock ? (
                  <Button
                    size="lg"
                    className="flex-1 rounded-full text-base font-bold gap-2 bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 border border-yellow-500/30"
                    onClick={handleStockAlert}
                    disabled={alertSubscribed || subscribingAlert}
                    data-testid="notify-me-button"
                  >
                    <Bell className="w-5 h-5" />
                    {alertSubscribed ? 'Subscribed!' : subscribingAlert ? 'Subscribing...' : 'Notify Me When Available'}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className="flex-1 btn-primary-glow rounded-full text-base font-bold gap-2"
                    onClick={handleAddToCart}
                    disabled={addingToCart}
                    data-testid="add-to-cart-button"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {addingToCart ? 'Adding...' : 'Add to Cart'}
                  </Button>
                )}
              </div>
              
              <div className="mt-8 p-6 rounded-xl bg-white/5 border border-white/10">
                <h3 className="font-heading font-bold mb-4">What You Get</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-accent" />
                    Instant email delivery
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-accent" />
                    100% verified account
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-accent" />
                    Secure payment via UPI
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Reviews Section */}
          <div className="mt-16">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-heading font-black">Customer Reviews</h2>
              {localStorage.getItem('token') && (
                <Button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  variant="outline"
                  className="rounded-full"
                  data-testid="write-review-button"
                >
                  Write a Review
                </Button>
              )}
            </div>
            
            {/* Review Form */}
            {showReviewForm && (
              <div className="mb-8 p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="review-form">
                <h3 className="text-xl font-heading font-bold mb-4">Write Your Review</h3>
                <form onSubmit={handleSubmitReview} className="space-y-4">
                  <div>
                    <Label>Rating</Label>
                    <div className="mt-2">
                      {renderStars(newReview.rating, true, (rating) => setNewReview({ ...newReview, rating }))}
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="review-comment">Your Review</Label>
                    <Textarea
                      id="review-comment"
                      value={newReview.comment}
                      onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                      required
                      rows={4}
                      className="bg-white/5 border-white/10 mt-2"
                      placeholder="Share your experience with this account..."
                      data-testid="review-comment-input"
                    />
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={submittingReview}
                      className="btn-primary-glow rounded-full"
                      data-testid="submit-review-button"
                    >
                      {submittingReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowReviewForm(false)}
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Reviews List */}
            {reviews.length === 0 ? (
              <div className="text-center py-12 p-6 rounded-2xl border border-white/10 bg-white/5">
                <p className="text-muted-foreground">No reviews yet. Be the first to review!</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="reviews-list">
                {reviews.map((review) => (
                  <div
                    key={review.review_id}
                    className="p-6 rounded-2xl border border-white/10 bg-white/5"
                    data-testid={`review-${review.review_id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-heading font-bold">{review.user_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      {renderStars(review.rating)}
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default AccountDetailPage;
